import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Box,
  Badge,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  FiFolder,
  FiUser,
  FiCalendar,
  FiFile,
  FiFolderPlus,
  FiHardDrive,
  FiFileText,
} from 'react-icons/fi'
import { FolderProperties } from '@/types'
import { apiService } from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface FolderPropertiesModalProps {
  isOpen: boolean
  onClose: () => void
  folderId: number
}

// Local formatting functions replaced with i18n versions

export default function FolderPropertiesModal({
  isOpen,
  onClose,
  folderId,
}: FolderPropertiesModalProps) {
  const [properties, setProperties] = useState<FolderProperties | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    t,
    formatFileSize: formatFileSizeI18n,
    formatDateTime,
  } = useAppTranslation(['files', 'common'])

  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')

  useEffect(() => {
    if (isOpen && folderId) {
      fetchFolderProperties()
    }
  }, [isOpen, folderId])

  const fetchFolderProperties = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await apiService.getFolderProperties(folderId)
      setProperties(data)
    } catch (err: any) {
      console.error('Failed to fetch folder properties:', err)
      setError(err.response?.data?.error || t('folderProperties.loadError', { ns: 'files' }))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setProperties(null)
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={bgColor} borderColor={borderColor}>
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Icon as={FiFolder} color="brand.500" />
            <Text>{t('folderProperties.title', { ns: 'files' })}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          {loading && (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" color="brand.500" />
              <Text color={mutedTextColor}>{t('folderProperties.loading', { ns: 'files' })}</Text>
            </VStack>
          )}

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {properties && !loading && (
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack spacing={2}>
                        <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                          {properties.name}
                        </Text>
                      </HStack>
                      {properties.description && (
                        <Text color={mutedTextColor} fontSize="sm">
                          {properties.description}
                        </Text>
                      )}
                      <Text color={mutedTextColor} fontSize="sm" fontFamily="mono">
                        {properties.path}
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Statistics Grid */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Stat>
                  <StatLabel>
                    <HStack spacing={2}>
                      <Icon as={FiFile} color="blue.500" />
                      <Text>{t('folderProperties.totalFiles', { ns: 'files' })}</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="blue.600">{properties.total_files}</StatNumber>
                  <StatHelpText>
                    {t('folderProperties.acrossAllSubfolders', { ns: 'files' })}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>
                    <HStack spacing={2}>
                      <Icon as={FiFolderPlus} color="purple.500" />
                      <Text>{t('folderProperties.subfolders', { ns: 'files' })}</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="purple.600">{properties.total_folders}</StatNumber>
                  <StatHelpText>
                    {t('folderProperties.nestedFolders', { ns: 'files' })}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>
                    <HStack spacing={2}>
                      <Icon as={FiHardDrive} color="green.500" />
                      <Text>{t('folderProperties.totalSize', { ns: 'files' })}</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="green.600">
                    {formatFileSizeI18n(properties.total_size)}
                  </StatNumber>
                  <StatHelpText>
                    {t('folderProperties.allFilesCombined', { ns: 'files' })}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>
                    <HStack spacing={2}>
                      <Icon as={FiFileText} color="orange.500" />
                      <Text>{t('folderProperties.totalPages', { ns: 'files' })}</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="orange.600">{properties.total_pages}</StatNumber>
                  <StatHelpText>
                    {t('folderProperties.documentPages', { ns: 'files' })}
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider />

              {/* Metadata */}
              <VStack spacing={4} align="stretch">
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  {t('folderProperties.details', { ns: 'files' })}
                </Text>

                <SimpleGrid columns={1} spacing={3}>
                  <Box>
                    <HStack spacing={2} align="start">
                      <Icon as={FiUser} color={mutedTextColor} mt={1} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                          {t('folderProperties.createdBy', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {properties.created_by.name} ({properties.created_by.email})
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Box>
                    <HStack spacing={2} align="start">
                      <Icon as={FiCalendar} color={mutedTextColor} mt={1} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                          {t('folderProperties.created', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {formatDateTime(new Date(properties.created_at))}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Box>
                    <HStack spacing={2} align="start">
                      <Icon as={FiCalendar} color={mutedTextColor} mt={1} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                          {t('folderProperties.lastUpdated', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor}>
                          {formatDateTime(new Date(properties.last_updated))}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </SimpleGrid>
              </VStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
